import React, { useEffect, useMemo, useState } from 'react';
import 'moment/locale/es';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { generateColumnsDefaultConfiguration } from '../../../components/dataGrid/columnHelpers';
import DataGridGenerico from '../../../components/dataGrid/DataGridGenerico';

import './generarMinutaGrilla.scss';
import moment from 'moment';
import { GenerarMinutaGrillaHeader } from './GenerarMinutaGrillaHeader';
import { GenerarMinutaGrillaFilters } from './GenerarMinutaGrillaFilters';
import { usePersistFilters } from '../../../hooks/usePersistFilters';
import { formatCurrency } from "../../../utils/mathUtils";
import { IconButton } from '@mui/material';

export const GenerarMinutaGrilla = ({ setChildrenAction }) => {

    const navigate = useNavigate();

    const [allowedWrite, setAllowedWrite] = useState(false);
    const [delegaciones, setDelegaciones] = useState([]);
    const [periodoActivo, setPeriodoActivo] = useState(null);
    const [conexionesPendientesFacturar, setConexionesPendientesFacturar] = useState([]);
    const [conexionesPendientesLoading, setConexionesPendientesLoading] = useState(false);
    const [conexionesSeleccionadas, setConexionesSeleccionadas] = useState([]);
    const [rowSelectionModel, setRowSelectionModel] = useState([]);
    const [refetchGrilla, setRefetchGrilla] = useState(false);
    const [generateRunning, setGenerateRunning] = useState(false);

    const actualMenos1Mes = moment().add(-1, 'months').format("YYYY").toString() + '-' + moment().add(-1, 'months').format("MMMM").toString() + '-01';
    const initFechaDesde = moment(actualMenos1Mes).format("YYYY-MM-DD");
    const initFechaHasta = moment(initFechaDesde).add(1, 'months').add(-1, 'days').format("YYYY-MM-DD");
    const { filters, handleChangeFilters } = usePersistFilters({
        fechaDesde: initFechaDesde,
        fechaHasta: initFechaHasta,
        idDelegacion: 0,
        mesesAnteriores: false,
        idCliente: "",
        idPaciente: ""
    }, "generarMinutaGrillaFilters");

    const { inputJustificacion, handleChangeJustificacion } = useState({
        idJustificacion: 0
    });

    const location = useLocation();
    const rutasStorage = localStorage.getItem('navigationRoutes');

    useEffect(() => {
        if (rutasStorage) {
            const rutas = JSON.parse(rutasStorage);
            const rutaQueSirve = location.pathname;
            setAllowedWrite(rutas?.find(ruta => ruta.aplicaciones.find(app => app.url == rutaQueSirve)
            )?.aplicaciones?.find(app => app.url == rutaQueSirve).escritura);
        }
    }, [rutasStorage]);

    useEffect(() => {
        setChildrenAction(() => (
            <GenerarMinutaGrillaHeader
                allowedWrite={allowedWrite}
                conexionesPendientesFacturar={conexionesPendientesFacturar}
                conexionesSeleccionadas={conexionesSeleccionadas}
                filters={filters}
                periodoActivo={periodoActivo}
                setPeriodoActivo={setPeriodoActivo}
                setRefetchGrilla={setRefetchGrilla}
                setGenerateRunning={setGenerateRunning}
            />
        ));
    }, [
        setChildrenAction,
        filters,
        conexionesPendientesFacturar,
        conexionesSeleccionadas,
        allowedWrite,
        periodoActivo
    ]);

    const handleClickAction = (row) => {
        navigate(location.pathname + '/detalle', {
            state: {
                allowedWrite: allowedWrite,
                dataRegistro: row,
                idDelegacion: filters?.idDelegacion,
                delegacion: delegaciones.filter(d => d.id == filters?.idDelegacion)[0]?.name,
                periodoActivo: periodoActivo
            }
        });
    }

    const disabledCheckbox = (row) => {
        return (!allowedWrite || (periodoActivo == null));
    }

    const handleSelectionModelChange = (ids) => {
        setRowSelectionModel(ids);
        if (ids.length > 0) {
            setConexionesSeleccionadas(conexionesPendientesFacturar.filter((row, index) => {
                return ids.includes(index + 1);
            }));
        } else {
            setConexionesSeleccionadas([]);
        }
    }

    const initialColumns = [
        {
            headerName: '',
            field: 'action',
            noPadding: true,
            flex: 0.03,
            minWidth: 35,
            maxWidth: 35,
            sortable: false,
            align: 'center',
            renderCell: (cellProps) => {
                const { row } = cellProps;
                return (
                    <IconButton onClick={() => handleClickAction(row)}>
                        <VisibilityIcon sx={{ fontSize: '13pt' }} />
                    </IconButton>
                )
            }
        },
        {
            field: 'idPaciente',
            headerName: 'HC',
            flex: 0.13
        },
        {
            field: 'paciente',
            headerName: 'Paciente',
            flex: 0.35
        },
        {
            field: 'codClienteSAP',
            headerName: 'Cod. Cliente',
            flex: 0.17
        },
        {
            field: 'cliente',
            headerName: 'Cliente',
            flex: 0.4
        },
        {
            field: 'tipoModulo',
            headerName: 'Prog.',
            flex: 0.1
        },
        {
            field: 'idEvaluacion',
            headerName: 'Contrato',
            flex: 0.13
        },
        {
            field: 'cantidadConexion',
            headerName: 'Cnxs',
            description: 'Conexiones',
            flex: 0.11
        },
        {
            field: 'precioDiario',
            headerName: '$ Unit.',
            flex: 0.17,
            align: 'right',
            headerAlign: 'right',
            valueGetter: (precioDiario) => {
                return formatCurrency(precioDiario);
            }
        },
        {
            field: 'precioSAP',
            headerName: '$ SAP',
            flex: 0.17,
            align: 'right',
            headerAlign: 'right',
            valueGetter: (precioSAP) => {
                return formatCurrency(precioSAP);
            }
        },
        {
            field: 'precioTotal',
            headerName: '$ Total',
            flex: 0.19,
            valueGetter: (precioTotal) => {
                return formatCurrency(precioTotal);
            }
        },
    ];

    const columns = useMemo(() => generateColumnsDefaultConfiguration(initialColumns), [filters, allowedWrite, periodoActivo]);

    return (
        <>
            <div>
                <GenerarMinutaGrillaFilters
                    delegaciones={delegaciones}
                    filters={filters}
                    handleChangeFilters={handleChangeFilters}
                    setConexionesPendientesFacturar={setConexionesPendientesFacturar}
                    setConexionesPendientesLoading={setConexionesPendientesLoading}
                    setDelegaciones={setDelegaciones}
                    refetchGrilla={refetchGrilla}
                    setRefetchGrilla={setRefetchGrilla}
                    generateRunning={generateRunning}
                    periodoActivo={periodoActivo}
                />
                <br />
                <DataGridGenerico
                    columns={columns}
                    rows={conexionesPendientesFacturar}
                    toolbar={false}
                    maxWidth={columns.reduce(
                        (total, column) => total + (column.width || 0),
                        0
                    )}
                    checkboxSelection={true}
                    isRowSelectable={(param) => !disabledCheckbox(param.row)}
                    onRowSelectionModelChange={handleSelectionModelChange}
                    rowSelectionModel={rowSelectionModel}
                    loading={conexionesPendientesLoading}
                />
            </div>
            <Outlet />
        </>
    )
}